import React from 'react';
import clsx from 'clsx';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import type { Breakpoint } from '@builder4/shared/types/layout';

// Outline (simple) icons
import IconDesktopOutline from '@/assets/icons/icon-desktop.svg?react';
import IconTabletOutline from '@/assets/icons/icon-tablet.svg?react';
import IconMobileOutline from '@/assets/icons/icon-mobile.svg?react';

// Filled icons
import IconDesktopFilled from '@/assets/icons/icon-desktop-filled.svg?react';
import IconTabletFilled from '@/assets/icons/icon-tablet-filled.svg?react';
import IconMobileFilled from '@/assets/icons/icon-mobile-filled.svg?react';

import IconDesktopNoVisible from '@/assets/icons/icon-desktop-no-visible.svg?react';
import IconTabletNoVisible from '@/assets/icons/icon-tablet-no-visible.svg?react';
import IconMobileNoVisible from '@/assets/icons/icon-mobile-no-visible.svg?react';

interface VisibilityProps {
  className?: string;
  text?: string;
  selected: Breakpoint[];
  onChange?: (value: Breakpoint[]) => void;
  selectionMode?: 'multi' | 'single';
  /** Tailwind classes applied to the sliding thumb in single-select mode */
  thumbTransitionClassName?: string;
  /** When true, inactive icons use outline set; when false (default), use no-visible set */
  inactiveUseOutline?: boolean;
}

export const Visibility: React.FC<VisibilityProps> = ({
  className = '',
  text = 'Visibility',
  selected,
  onChange,
  selectionMode = 'multi',
  thumbTransitionClassName = 'transition-transform duration-200 ease-out',
  inactiveUseOutline = false,
}) => {
  const deviceOrder: Breakpoint[] = ['desktop', 'tablet', 'mobile'];
  const selectedIndex =
    selectionMode === 'single' ? Math.max(0, deviceOrder.indexOf(selected[0] ?? 'desktop')) : -1;
  // Toggle the selected device in the array
  const handleToggle = (value: Breakpoint) => {
    if (selectionMode === 'single') {
      // In single mode, only one device can be active at a time.
      const updated: Breakpoint[] = [value];
      onChange?.(updated);
      return;
    }
    // multi-select behavior (default)
    const updated = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange?.(updated);
  };

  const renderButton = (device: Breakpoint) => {
    const isSingle = selectionMode === 'single';
    const isSelected = isSingle
      ? selected.length > 0
        ? selected[0] === device
        : device === 'desktop'
      : selected.includes(device);
    // Active icons always use filled set
    const activeIcons = {
      desktop: IconDesktopFilled,
      tablet: IconTabletFilled,
      mobile: IconMobileFilled,
    };

    // Inactive icons: outline when requested, otherwise "no visible" set
    const inactiveIcons = inactiveUseOutline
      ? {
          desktop: IconDesktopOutline,
          tablet: IconTabletOutline,
          mobile: IconMobileOutline,
        }
      : {
          desktop: IconDesktopNoVisible,
          tablet: IconTabletNoVisible,
          mobile: IconMobileNoVisible,
        };

    const Icon = isSelected ? activeIcons[device] : inactiveIcons[device];

    return (
      <button
        key={device}
        type="button"
        onClick={() => handleToggle(device)}
        data-testid={`visibility-${device}`}
        className={clsx(
          'relative z-10 inline-flex h-8 w-8 cursor-pointer items-center justify-center gap-2.5 rounded-md py-1.5',
          {
            // Per-button outline only in multi-select mode
            'rounded-md bg-white outline-1 outline-neutral-100':
              selectionMode === 'multi' &&
              selected.includes(device) &&
              (isDesktopAndMobile || (!isDesktopAndTablet && !isTabletAndMobile && !isAllSelected)),
          },
        )}
        aria-pressed={isSelected}
      >
        <Icon
          className={clsx('h-4.5 w-4.5', {
            'text-indigo-500': isSelected, // Highlight icon if selected
            'text-neutral-400': !isSelected, // Grey icon if not selected
          })}
        />
      </button>
    );
  };

  const has = (device: Breakpoint) => selected.includes(device);

  // Combinations of selected devices
  const isDesktopAndMobile = has('desktop') && has('mobile') && !has('tablet');
  const isDesktopAndTablet = has('desktop') && has('tablet') && !has('mobile');
  const isTabletAndMobile = has('tablet') && has('mobile') && !has('desktop');
  const isAllSelected = has('desktop') && has('tablet') && has('mobile');

  // Render a group of buttons wrapped with outline and background if needed
  const renderGroup = (devices: Breakpoint[], isOutlined: boolean) => (
    <div
      className={clsx({
        'rounded-md bg-white outline-1 outline-neutral-100': isOutlined,
      })}
    >
      {devices.map(renderButton)}
    </div>
  );

  return (
    <div className={clsx('flex w-full items-center justify-between gap-3', className)}>
      {text && (
        <div className="flex items-center gap-1">
          <p className="text-button-sm font-medium text-neutral-500">{text}</p>
          <InfoTooltip text="Show or hide this element on specific device sizes. Click to toggle visibility for desktop, tablet, or mobile views." />
        </div>
      )}

      <div
        className={clsx(
          'relative inline-flex h-8 items-center justify-start rounded-md bg-neutral-50 shadow-inner',
        )}
      >
        {selectionMode === 'single' && (
          <div
            className={clsx(
              'pointer-events-none absolute top-0 left-0 h-8 w-8 rounded-md bg-white outline-1 outline-neutral-100',
              thumbTransitionClassName,
            )}
            style={{ transform: `translateX(${selectedIndex * 32}px)` }}
          />
        )}
        {/* desktop + tablet */}
        {selectionMode === 'multi' && isDesktopAndTablet && (
          <>
            {renderGroup(['desktop', 'tablet'], true)}
            {renderButton('mobile')}
          </>
        )}

        {/* tablet + mobile */}
        {selectionMode === 'multi' && isTabletAndMobile && (
          <>
            {renderButton('desktop')}
            {renderGroup(['tablet', 'mobile'], true)}
          </>
        )}

        {/* all three selected */}
        {selectionMode === 'multi' && isAllSelected && (
          <>{renderGroup(['desktop', 'tablet', 'mobile'], true)}</>
        )}

        {/* default - individual buttons */}
        {(selectionMode === 'single' ||
          (!isDesktopAndTablet && !isTabletAndMobile && !isAllSelected)) && (
          <>
            {renderButton('desktop')}
            {renderButton('tablet')}
            {renderButton('mobile')}
          </>
        )}
      </div>
    </div>
  );
};
